import React, { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Card, Button, Alert, Spinner, Row, Col, Badge, Table, Form } from 'react-bootstrap'
import { useAdminTableColumn } from 'api/hooks/use-admin-table-columns'
import SystemTableColumnsList from './SystemTableColumnsList'
import AdminTabs from 'admin/shared/AdminTabs'
import AppBadge from 'shared/badges/app-badge'

const TableColumnDetail = () => {
  const { id } = useParams()
  const { data: tableColumn, isLoading, error } = useAdminTableColumn({ id })
  const [activeTab, setActiveTab] = useState('details')
  const [showOffAnalytics, setShowOffAnalytics] = useState(false)

  if (isLoading) {
    return (
      <div className="text-center p-5">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </div>
    )
  }

  if (error) {
    return (
      <Alert variant="danger">
        Error loading table column: {error.message}
      </Alert>
    )
  }

  if (!tableColumn) {
    return <Alert variant="warning">Table column not found.</Alert>
  }

  const filteredAnalytics = tableColumn.analytics?.filter((a) => showOffAnalytics || a.is_core) || []

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>{tableColumn.name}</h2>
        <div>
          <Button as={Link} to={`/admin/table-columns/${id}/edit`} variant="warning" className="me-2">
            Edit Table Column
          </Button>
          <Button as={Link} to="/admin/table-columns" variant="light-primary">
            Back to List
          </Button>
        </div>
      </div>

      <AdminTabs
        tabs={[
          { name: 'Details', value: 'details' },
          { name: `Analytics (${filteredAnalytics.length})`, value: 'analytics' },
          { name: `System Columns (${tableColumn.system_table_columns_count || 0})`, value: 'system-columns' }
        ]}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
      />

      {activeTab === 'details' && (
        <Row>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Basic Information</h5></Card.Header>
              <Card.Body>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Name:</dt>
                  <dd className="col-sm-7">{tableColumn.name}</dd>
                  <dt className="col-sm-5">Database Column:</dt>
                  <dd className="col-sm-7"><code>{tableColumn.database_column_name || 'N/A'}</code></dd>
                  <dt className="col-sm-5">Module:</dt>
                  <dd className="col-sm-7">
                    {tableColumn.section_abbreviation ? (
                      <Badge bg="info">{tableColumn.section_abbreviation}</Badge>
                    ) : (
                      <Badge bg="secondary">Global</Badge>
                    )}
                  </dd>
                  <dt className="col-sm-5">Accessor Key:</dt>
                  <dd className="col-sm-7">{tableColumn.accessor_key || 'N/A'}</dd>
                  <dt className="col-sm-5">Description:</dt>
                  <dd className="col-sm-7">{tableColumn.description || 'N/A'}</dd>
                </dl>
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card className="mb-4">
              <Card.Header><h5>Display & Filter Settings</h5></Card.Header>
              <Card.Body>
                <dl className="row mb-0">
                  <dt className="col-sm-5">Display As:</dt>
                  <dd className="col-sm-7">{tableColumn.display_as || 'N/A'}</dd>
                  <dt className="col-sm-5">Filter Type:</dt>
                  <dd className="col-sm-7">{tableColumn.filter_type || 'N/A'}</dd>
                  <dt className="col-sm-5">Grouping:</dt>
                  <dd className="col-sm-7">{tableColumn.grouping || 'N/A'}</dd>
                  <dt className="col-sm-5">Grain:</dt>
                  <dd className="col-sm-7">{tableColumn.grain || 'Same as its module'}</dd>
                  <dt className="col-sm-5">Role:</dt>
                  <dd className="col-sm-7">
                    {tableColumn.effective_role || 'N/A'}
                    {!tableColumn.role && tableColumn.effective_role && (
                      <AppBadge variant="secondary" className="ms-2">derived</AppBadge>
                    )}
                  </dd>
                  <dt className="col-sm-5">Default Aggregation:</dt>
                  <dd className="col-sm-7">
                    {tableColumn.effective_role === 'measure' ? tableColumn.effective_default_aggregation : 'N/A'}
                    {tableColumn.effective_role === 'measure' && !tableColumn.default_aggregation && (
                      <AppBadge variant="secondary" className="ms-2">derived</AppBadge>
                    )}
                  </dd>
                  <dt className="col-sm-5">Is Default:</dt>
                  <dd className="col-sm-7">
                    {tableColumn.is_default ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}
                  </dd>
                  <dt className="col-sm-5">Is Sortable:</dt>
                  <dd className="col-sm-7">
                    {tableColumn.is_sortable ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}
                  </dd>
                  <dt className="col-sm-5">Is Selectable:</dt>
                  <dd className="col-sm-7">
                    {tableColumn.is_selectable ? <Badge bg="success">Yes</Badge> : <Badge bg="secondary">No</Badge>}
                  </dd>
                </dl>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {activeTab === 'analytics' && (
        <Card>
          <Card.Body>
            <div className="d-flex justify-content-end mb-3">
              <Form.Check
                type="switch"
                label="Show Off Analytics"
                checked={showOffAnalytics}
                onChange={(e) => setShowOffAnalytics(e.target.checked)}
              />
            </div>
            {filteredAnalytics.length > 0 ? (
              <Table bordered hover responsive>
                <thead>
                  <tr>
                    <th>Key</th>
                    <th>Name</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAnalytics.map((analytic) => (
                    <tr key={analytic.id}>
                      <td><code>{analytic.key}</code></td>
                      <td>{analytic.name}</td>
                      <td><span className="text-muted">{analytic.is_core ? 'On' : 'Off'}</span></td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        <Button as={Link} to={`/admin/analytics-data/${analytic.id}`} variant="info" size="sm" className="me-2">View</Button>
                        <Button as={Link} to={`/admin/analytics-data/${analytic.id}/edit`} variant="warning" size="sm">Edit</Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Alert variant="info" className="mb-0">No analytics use this table column.</Alert>
            )}
          </Card.Body>
        </Card>
      )}

      {activeTab === 'system-columns' && (
        <Card>
          <Card.Body>
            <SystemTableColumnsList tableColumnId={id} filterType={tableColumn.filter_type} />
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default TableColumnDetail
